"use client";

import {
	RiAppleFill,
	RiHeadphoneFill,
	RiPlayListFill,
	RiSoundcloudFill,
	RiYoutubeFill,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo } from "react";

export interface SetSocialButtonsProps {
	setName: string; // For accessibility (e.g., "Martin Garrix at Ultra Miami")
	links: {
		youtube?: string | null;
		soundcloud?: string | null;
		appleMusic?: string | null;
		oneThousandOneTracklists?: string | null;
		setlistFm?: string | null;
	};
	size?: "xs" | "sm" | "md" | "lg";
	className?: string;
	maxVisible?: number; // Limit number of visible buttons
}

const sizeClasses = {
	xs: {
		gap: "gap-2",
		buttonSize: "iconSm" as const,
	},
	sm: {
		gap: "gap-1",
		buttonSize: "icon" as const,
	},
	md: {
		gap: "gap-1",
		buttonSize: "icon" as const,
	},
	lg: {
		gap: "gap-2",
		buttonSize: "icon" as const,
	},
};

const setSocialPlatforms = [
	{ key: "youtube", icon: RiYoutubeFill, label: "YouTube" },
	{ key: "soundcloud", icon: RiSoundcloudFill, label: "SoundCloud" },
	{ key: "appleMusic", icon: RiAppleFill, label: "Apple Music" },
	{
		key: "oneThousandOneTracklists",
		icon: RiHeadphoneFill,
		label: "1001Tracklists",
	},
	{ key: "setlistFm", icon: RiPlayListFill, label: "setlist.fm" },
] as const;

export const SetSocialButtons = memo<SetSocialButtonsProps>(
	function SetSocialButtons({
		setName,
		links,
		size = "md",
		className,
		maxVisible,
	}) {
		// Filter out platforms with no URL
		const availablePlatforms = setSocialPlatforms.filter(
			(platform) => links[platform.key as keyof typeof links],
		);

		if (availablePlatforms.length === 0) return null;

		// Apply maxVisible limit if specified
		const visiblePlatforms = maxVisible
			? availablePlatforms.slice(0, maxVisible)
			: availablePlatforms;

		const sizeConfig = sizeClasses[size];

		return (
			<div
				className={cn("flex flex-wrap items-center", sizeConfig.gap, className)}
			>
				{visiblePlatforms.map(({ key, icon: Icon, label }) => {
					const url = links[key as keyof typeof links];
					if (!url) return null;

					return (
						<Button key={key} asChild variant="secondary" size={sizeConfig.buttonSize}>
							<Link href={url} target="_blank" rel="noopener noreferrer">
								<Icon />
								<span className="sr-only">
									{setName} on {label}
								</span>
							</Link>
						</Button>
					);
				})}
			</div>
		);
	},
);

SetSocialButtons.displayName = "SetSocialButtons";
